import { useEffect, useLayoutEffect, useRef, useState } from 'react';

// Next.js ignores history entries that don't carry its own state. If we pushed `null`, pressing
// back from another page onto this entry would change the URL but keep showing the other page.
const toNextRouterState = (
  currentState: { __N?: boolean; url?: string } | null,
  name: string,
  value: string | null,
  as: string,
) => {
  // eslint-disable-next-line no-underscore-dangle
  if (!currentState?.__N || !currentState.url) return null;

  const url = new URL(currentState.url, window.location.origin);
  if (value) {
    url.searchParams.set(name, value);
  } else {
    url.searchParams.delete(name);
  }

  return { ...currentState, url: url.pathname + url.search + url.hash, as };
};

export default function useQueryParam<const T>({
  name,
  parse,
  stringify = (value: T) => String(value),
  defaultValue,
  scroll,
  onChange,
  rememberHistory = true,
}: {
  name?: string;
  parse: (value: string) => T;
  stringify?: (value: T) => string;
  defaultValue: T;
  scroll?: boolean;
  onChange?: (value: T) => void;
  rememberHistory?: boolean;
}) {
  const parseRef = useRef(parse);
  parseRef.current = parse;
  const stringifyRef = useRef(stringify);
  stringifyRef.current = stringify;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [value, setValue] = useState<T>(() => {
    if (!name) return defaultValue;
    // read from window.location because router.query is not initialized during initial render
    const searchParams = new URLSearchParams(window.location.search);
    const queryValue = searchParams.get(name);
    return queryValue ? parse(queryValue) : defaultValue;
  });

  // Sync state → URL using the History API directly.
  // useLayoutEffect ensures pushState runs before any useEffect (e.g. useRemotePagination's
  // router.replace), preventing other hooks from clobbering URL params with stale router.query.
  useLayoutEffect(() => {
    if (!name) return;

    const url = new URL(window.location.href);
    const currentUrlValue = url.searchParams.get(name);
    const currentStringValue = value !== defaultValue ? stringifyRef.current(value) : null;

    if (currentStringValue !== currentUrlValue) {
      if (currentStringValue) {
        url.searchParams.set(name, currentStringValue);
      } else {
        url.searchParams.delete(name);
      }

      const newUrl = url.pathname + url.search + url.hash;
      const state = toNextRouterState(window.history.state, name, currentStringValue, newUrl);
      if (rememberHistory) {
        window.history.pushState(state, '', newUrl);
      } else {
        window.history.replaceState(state, '', newUrl);
      }

      if (scroll) {
        window.scrollTo(0, 0);
      }
    }
  }, [name, value, defaultValue, scroll, rememberHistory]);

  // Sync URL → state (back/forward navigation)
  useEffect(() => {
    if (!name) return;

    const handlePopState = () => {
      const searchParams = new URLSearchParams(window.location.search);
      const queryValue = searchParams.get(name);
      setValue(queryValue ? parseRef.current(queryValue) : defaultValue);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [name, defaultValue]);

  useEffect(() => {
    onChangeRef.current?.(value);
  }, [value]);

  return [value, setValue] as const;
}
